import type { Properties as CSSProperties } from "csstype";

/**
 * TextSize enum defines the sizes that can be used for the Text component.
 * Options include LARGE, MEDIUM, SMALL, X-SMALL, and XX-SMALL.
 */
export enum TextSize {
    LARGE = "large",
    MEDIUM = "medium",
    SMALL = "small",
    "X-SMALL" = "x-small",
    "XX-SMALL" = "xx-small",
}

/**
 * TextWeight enum defines the weight options that can be used for the Text component.
 * Options include BOLD, SEMIBOLD, and NORMAL.
 */
export enum TextWeight {
    BOLD = "bold",
    SEMIBOLD = "semibold",
    NORMAL = "normal",
}

/**
 * TextProps are the properties used by the Text component.
 */
export interface TextProps extends React.HTMLAttributes<HTMLParagraphElement> {
    /**
     * Specifies the size of the text.
     * @default TextSize.MEDIUM
     */
    size?: TextSize;

    /**
     * Specifies the weight of the text.
     * @default TextWeight.NORMAL
     */
    weight?: TextWeight;

    /**
     * Defines the color of the text.
     * @default inherit
     */
    color?: string;

    /**
     * Specifies the alignment of the text.
     * @default inherit
     */
    align?: CSSProperties["textAlign"];

    /**
     * Controls the white-space handling behavior.
     */
    whiteSpace?: CSSProperties["whiteSpace"];

    /**
     * Controls the line breaking rules.
     */
    wordBreak?: CSSProperties["wordBreak"];

    /**
     * If true, the text is transformed to uppercase.
     */
    toUpperCase?: boolean;

    /**
     * If true, the text is capitalized.
     * @default false
     */
    capitalize?: boolean;

    /**
     * If true, the text is strike trough.
     * @default false
     */
    strikeTrough?: boolean;

    as?: keyof JSX.IntrinsicElements;
    fontStyle?: CSSProperties["fontStyle"];
}

/**
 * DefaultTextProps are the default properties for size, weight, color, and alignment in the Text component.
 */
export type DefaultTextProps = {
    /**
     * Default size for the text.
     */
    size: NonNullable<TextProps["size"]>;

    /**
     * Default weight for the text.
     */
    weight: NonNullable<TextProps["weight"]>;

    /**
     * Default color for the text.
     */
    color: NonNullable<TextProps["color"]>;

    /**
     * Default alignment for the text.
     */
    align: NonNullable<TextProps["align"]>;
    fontStyle: NonNullable<TextProps["fontStyle"]>;
};
